/**
 * Sidebar Component
 * АРМ Гроднооблавтотранс - Collapsible Sidebar with Alpine.js
 * 
 * Features:
 * - Collapsible state with localStorage persistence
 * - Mobile offcanvas support
 * - Scroll indicator
 * - Tooltip support for collapsed state
 */

// ============================================
// SIDEBAR STATE MANAGEMENT
// ============================================

const SIDEBAR_STORAGE_KEY = 'sidebar_collapsed';

/**
 * Get initial collapsed state from localStorage
 * @returns {boolean}
 */
function getInitialCollapsedState() {
    try {
        const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        return stored === 'true';
    } catch (e) {
        return false;
    }
}

/**
 * Save collapsed state to localStorage
 * @param {boolean} collapsed
 */
function saveCollapsedState(collapsed) {
    try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed.toString());
    } catch (e) {
        console.warn('Failed to save sidebar state:', e);
    }
}

// ============================================
// SIDEBAR DATA COMPONENT DEFINITION
// ============================================

function sidebarComponent() {
    return {
        // State
        collapsed: false,
        mobileOpen: false,
        isMobile: false,
        
        // Initialization
        init() {
            // Check if mobile
            this.checkMobile();
            
            // Set initial state (only on desktop)
            if (!this.isMobile) {
                this.collapsed = getInitialCollapsedState();
            }
            
            // Apply initial state to DOM
            this.applyState();
            
            // Listen for resize events
            window.addEventListener('resize', () => {
                this.checkMobile();
            });
            
            // Listen for mobile toggle events
            window.addEventListener('sidebar:toggle-mobile', () => {
                this.toggleMobile();
            });
            
            // Listen for close events
            window.addEventListener('sidebar:close-mobile', () => {
                this.closeMobile();
            });
        },
        
        // Check if viewport is mobile
        checkMobile() {
            this.isMobile = window.innerWidth < 992;
            
            // Reset states when switching between mobile/desktop
            if (this.isMobile) {
                this.collapsed = false;
            }
            
            this.applyState();
        },
        
        // Toggle collapsed state (desktop only)
        toggle() {
            if (this.isMobile) return;
            
            this.collapsed = !this.collapsed;
            this.applyState();
            saveCollapsedState(this.collapsed);
        },
        
        // Toggle mobile offcanvas
        toggleMobile() {
            if (!this.isMobile) return;
            
            this.mobileOpen = !this.mobileOpen;
            this.applyState();
        },
        
        // Close mobile sidebar
        closeMobile() {
            this.mobileOpen = false;
            this.applyState();
        },
        
        // Apply state to DOM elements
        applyState() {
            const sidebar = this.$el;
            const overlay = document.querySelector('.sidebar-overlay');
            
            if (!sidebar) return;
            
            // Handle collapsed state (desktop)
            if (this.collapsed && !this.isMobile) {
                sidebar.classList.add('collapsed');
                document.body.classList.add('sidebar-collapsed');
            } else {
                sidebar.classList.remove('collapsed');
                document.body.classList.remove('sidebar-collapsed');
            }
            
            // Handle mobile state
            if (this.isMobile) {
                if (this.mobileOpen) {
                    sidebar.classList.add('show');
                    document.body.style.overflow = 'hidden';
                    if (overlay) overlay.style.display = 'block';
                } else {
                    sidebar.classList.remove('show');
                    document.body.style.overflow = '';
                    if (overlay) overlay.style.display = '';
                }
            } else {
                // Ensure overlay is hidden on desktop
                if (overlay) overlay.style.display = '';
            }
        }
    };
}

// ============================================
// REGISTER ALPINE COMPONENT (if loaded directly)
// ============================================

// Note: Component is also registered in alpine/init.js for proper loading order
// This is a fallback for direct loading
if (window.Alpine && !window.Alpine._sidebarRegistered) {
    window.Alpine._sidebarRegistered = true;
}

// ============================================
// SCROLL INDICATOR
// ============================================

document.addEventListener('DOMContentLoaded', function() {
    const nav = document.getElementById('sidebar-nav');
    const indicator = document.getElementById('scroll-indicator');

    if (nav && indicator) {
        // Throttle helper для производительности
        function throttle(func, limit) {
            let inThrottle;
            return function(...args) {
                if (!inThrottle) {
                    func.apply(this, args);
                    inThrottle = true;
                    setTimeout(() => inThrottle = false, limit);
                }
            };
        }

        // Проверяем, нужна ли прокрутка
        function checkScroll() {
            if (nav.scrollHeight > nav.clientHeight) {
                indicator.classList.add('visible');
            } else {
                indicator.classList.remove('visible');
            }
        }

        // Проверяем при загрузке
        checkScroll();

        // Проверяем при изменении размера окна с debounce
        let resizeTimeout;
        window.addEventListener('resize', function() {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(checkScroll, 100);
        });

        // Скрываем индикатор когда доскроллили до конца (с throttle)
        nav.addEventListener('scroll', throttle(function() {
            if (nav.scrollTop + nav.clientHeight >= nav.scrollHeight - 10) {
                indicator.classList.remove('visible');
            } else {
                indicator.classList.add('visible');
            }
        }, 100));
    }
});


// BOOTSTRAP TOOLTIPS FOR SIDEBAR NAVIGATION
// ============================================

document.addEventListener('DOMContentLoaded', function() {
    // Initialize Bootstrap tooltips for sidebar navigation
    const sidebarNav = document.getElementById('sidebar-nav');
    if (sidebarNav && typeof bootstrap !== 'undefined') {
        // Find all nav links with data-title attribute (exclude group toggles — they have CSS tooltips)
        const navLinks = sidebarNav.querySelectorAll('.nav-link[data-title]:not(.sidebar-group-toggle)');
        
        navLinks.forEach(link => {
            // Create tooltip with custom options
            const tooltip = new bootstrap.Tooltip(link, {
                placement: 'right',
                trigger: 'hover',
                delay: { show: 200, hide: 100 },
                customClass: 'sidebar-nav-tooltip',
                container: 'body',
                popperConfig: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 10]
                            }
                        }
                    ]
                }
            });
            
            // Store tooltip instance on the element
            link._tooltip = tooltip;
            
            // Add cosmic entrance animation
            link.addEventListener('mouseenter', function() {
                const tooltipEl = document.querySelector('.sidebar-nav-tooltip.show');
                if (tooltipEl) {
                    tooltipEl.style.opacity = '0';
                    tooltipEl.style.transform = 'translateY(-50%) scale(0.9)';
                    
                    requestAnimationFrame(() => {
                        tooltipEl.style.transition = 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.transform = 'translateY(-50%) scale(1)';
                    });
                }
            });
        });
        
        // Handle sidebar collapse state changes
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const isCollapsed = sidebar.classList.contains('collapsed');
                        
                        navLinks.forEach(link => {
                            if (link._tooltip) {
                                if (isCollapsed) {
                                    // Enable tooltips when collapsed
                                    link._tooltip.enable();
                                } else {
                                    // Disable tooltips when expanded (show text instead)
                                    link._tooltip.hide();
                                    link._tooltip.disable();
                                }
                            }
                        });
                    }
                });
            });
            
            observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
            
            // Initial state check
            const isInitiallyCollapsed = sidebar.classList.contains('collapsed');
            navLinks.forEach(link => {
                if (link._tooltip) {
                    if (isInitiallyCollapsed) {
                        link._tooltip.enable();
                    } else {
                        link._tooltip.disable();
                    }
                }
            });
        }
    }
});

// ============================================
// EXPORTS
// ============================================

window.SidebarManager = {
    toggle() {
        window.dispatchEvent(new CustomEvent('sidebar:toggle'));
    },
    
    toggleMobile() {
        window.dispatchEvent(new CustomEvent('sidebar:toggle-mobile'));
    },
    
    closeMobile() {
        window.dispatchEvent(new CustomEvent('sidebar:close-mobile'));
    },
    
    collapse() {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar && sidebar._x_dataStack && sidebar._x_dataStack[0]) {
            const data = sidebar._x_dataStack[0];
            if (!data.collapsed) data.toggle();
        }
    },
    
    expand() {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar && sidebar._x_dataStack && sidebar._x_dataStack[0]) {
            const data = sidebar._x_dataStack[0];
            if (data.collapsed) data.toggle();
        }
    }
};

// Default export for module systems
export default sidebarComponent;


